import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Calendar, Phone, Mail, Facebook, Instagram, Youtube, X as XIcon, Menu as MenuIcon, ChevronDown, ChevronUp } from 'lucide-react';
import MenuItem from './MenuItem';
import AnimatedLogo from './AnimatedLogo';

const menuItems = [
  { label: 'होम', href: '/', isHash: false },
  { label: 'परिचय', href: '/about', isHash: false },
  { label: 'कार्य', href: '/karya', isHash: false },
  { label: 'रचनाएँ', href: '/rachnaye', isHash: false },
  { label: 'ब्लॉग', href: '/blog', isHash: false },
  { label: 'हमसे जुड़ें', href: '/joinus', isHash: false },
  { label: 'संपर्क', href: '/contact', isHash: false },
];

const mediaDropdown = [
  { label: 'वीडियो', href: '/videos', isHash: false },
  { label: 'गैलरी', href: '/gallery', isHash: false },
  { label: 'प्रेस', href: '/press', isHash: false },
];

const toHindiDigits = (str) => str.replace(/[0-9]/g, d => '०१२३४५६७८९'[d]);

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mediaDropdownOpen, setMediaDropdownOpen] = useState(false);
  const [showTopbar, setShowTopbar] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
        setMediaDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMediaDropdownOpen(false);
  }, [location]);

  // Hide topbar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowTopbar(window.scrollY < 40);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavigation = (href, isHash = false) => {
    if (isHash) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(href);
    }
    setMobileMenuOpen(false);
  };

  const toggleMediaDropdown = (e) => {
    e.stopPropagation();
    setMediaDropdownOpen(!mediaDropdownOpen);
  };

  // Active path handling
  const active = location.pathname === '/' ? location.hash || '/' : location.pathname;

  return (
    <header ref={menuRef} className="w-full bg-white shadow-md fixed top-0 left-0 right-0 z-50" style={{ fontFamily: 'Mukta, Arial, sans-serif' }}>
      {/* Top bar */}
      <div className={`${showTopbar ? 'h-6' : 'h-0'} bg-orange-600 text-white text-xs md:text-sm transition-all duration-300 overflow-hidden`}>
        <div className="container mx-auto h-full flex items-center justify-between px-4">
          <div className="flex items-center space-x-4">
            <a href="tel:+919423014414" className="flex items-center hover:text-orange-200 transition-colors">
              <Phone className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">{toHindiDigits('९४२३०१४४१४')}</span>
            </a>
            <a href="mailto:info@sidhisochsidhibaat.co.in" className="flex items-center hover:text-orange-200 transition-colors">
              <Mail className="w-3 h-3 mr-1" />
              <span className="hidden sm:inline">info@sidhisochsidhibaat.co.in</span>
            </a>
          </div>
          <div className="flex items-center space-x-3">
            <a href="https://facebook.com/sidhisochsidhibaat" target="_blank" rel="noopener noreferrer" className="text-white hover:text-orange-200 transition-colors">
              <Facebook className="w-3 h-3" />
            </a>
            <a href="https://instagram.com/sidhisochsidhibaat" target="_blank" rel="noopener noreferrer" className="text-white hover:text-orange-200 transition-colors">
              <Instagram className="w-3 h-3" />
            </a>
            <a href="https://youtube.com/@sidhisochsidhibaat" target="_blank" rel="noopener noreferrer" className="text-white hover:text-orange-200 transition-colors">
              <Youtube className="w-3 h-3" />
            </a>
            <a href="https://x.com/sidhisochsidhibaat" target="_blank" rel="noopener noreferrer" className="text-white hover:text-orange-200 transition-colors">
              <XIcon className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>

      {/* Main navbar */}
      <nav className="flex items-center justify-between h-16 px-4 md:px-8 bg-white border-b border-orange-100">
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" onClick={() => setMobileMenuOpen(false)}>
          <AnimatedLogo />
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center space-x-8">
          {menuItems.map((item) => (
            <MenuItem
              key={item.label}
              label={item.label}
              href={item.href}
              isActive={active === item.href}
              isHash={item.isHash}
              className="font-bold text-gray-800 hover:text-orange-600"
            />
          ))}
          
          <div className="relative group">
            <button
              className={`flex items-center space-x-1 px-3 py-2 rounded-lg font-bold text-base ${
                mediaDropdown.some(item => active === item.href) ? 'text-orange-600 bg-orange-50' : 'text-gray-800 hover:text-orange-600'
              }`}
              onClick={toggleMediaDropdown}
            >
              <span>मीडिया</span>
              {mediaDropdownOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {mediaDropdownOpen && (
              <div className="absolute left-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                {mediaDropdown.map((item) => (
                  <MenuItem
                    key={item.label}
                    label={item.label}
                    href={item.href}
                    isActive={active === item.href}
                    isHash={item.isHash}
                    className="block px-4 py-2 text-gray-700 hover:bg-orange-50 hover:text-orange-600"
                  />
                ))}
              </div>
            )}
          </div>

          <Link
            to="/#appointment"
            className="ml-4 px-4 py-2 bg-orange-500 text-white rounded-full text-sm font-bold hover:bg-orange-600 flex items-center whitespace-nowrap"
          >
            <Calendar className="w-4 h-4 mr-1" />
            अपॉइंटमेंट
          </Link>
        </div>

        {/* Mobile menu button */}
        <div className="flex items-center lg:hidden">
          <Link
            to="/#appointment"
            className="p-2 mr-2 bg-orange-500 text-white rounded-full"
            onClick={() => setMobileMenuOpen(false)}
          >
            <Calendar className="w-5 h-5" />
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-gray-700 hover:text-orange-600 focus:outline-none"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <XIcon className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`lg:hidden bg-white shadow-lg fixed inset-0 z-40 transform transition-transform duration-300 ease-in-out ${
        mobileMenuOpen ? 'translate-y-0' : '-translate-y-full hidden'
      }`}>
        <div className="h-full flex flex-col">
          <div className="p-4 border-b border-gray-200 flex justify-end">
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100"
              aria-label="Close menu"
            >
              <XIcon className="w-6 h-6 text-gray-700" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {menuItems.map((item) => (
              <div
                key={item.label}
                className={`block px-6 py-3 text-base font-medium ${
                  active === item.href ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => handleNavigation(item.href, item.isHash)}
              >
                {item.label}
              </div>
            ))}

            <div className="border-t border-gray-200 my-2"></div>

            <div>
              <button
                onClick={toggleMediaDropdown}
                className="w-full flex justify-between items-center px-6 py-3 text-base font-medium text-gray-700 hover:bg-gray-50"
              >
                <span>मीडिया</span>
                {mediaDropdownOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>
              {mediaDropdownOpen && (
                <div className="pl-6">
                  {mediaDropdown.map((item) => (
                    <div
                      key={item.label}
                      className={`block px-6 py-3 text-base ${
                        active === item.href ? 'text-orange-600 bg-orange-50' : 'text-gray-700 hover:bg-gray-50'
                      }`}
                      onClick={() => handleNavigation(item.href, item.isHash)}
                    >
                      {item.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 my-2"></div>

            <a
              href="https://newroadtransport.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="block px-6 py-3 text-base font-medium text-gray-700 hover:bg-gray-50"
              onClick={() => setMobileMenuOpen(false)}
            >
              सड़क और यातायात
            </a>
          </div>
          
          {/* Fixed bottom CTA */}
          <div className="p-4 border-t border-gray-200 bg-white">
            <Link
              to="/#appointment"
              className="flex items-center justify-center gap-2 bg-gradient-to-r from-orange-600 to-orange-500 text-white px-4 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
              onClick={() => setMobileMenuOpen(false)}
            >
              <Calendar className="w-5 h-5" />
              <span>अपॉइंटमेंट बुक करें</span>
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;